import { Fragment, useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/axios';
import { useAuth } from '../provider/AuthProvider';
import AdminBreadcrumb from '../components/AdminBreadcrumb';

// Page que la lambda parcourt chaque nuit. Valeur tenue par
// lambda-crawl/crawl-nains.js (mainPageUrl) : à garder alignée si elle change.
const URL_CATALOGUE_SOURCE = 'https://www.monde-aquilon.com/catalogue/';

const LIBELLES_ISSUE = {
    created: 'Ajouté',
    updated: 'Mis à jour',
    failed: 'En échec',
    skipped: 'Inchangé',
};

const CLASSES_ISSUE = {
    created: 'is-success',
    updated: 'is-info',
    failed: 'is-danger',
    skipped: 'is-light',
};

function formatDate(iso) {
    const date = new Date(iso);

    return Number.isNaN(date.getTime())
        ? iso
        : date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function Issue({ outcome }) {
    return (
        <span className={`tag ${CLASSES_ISSUE[outcome] ?? 'is-light'}`}>
            {LIBELLES_ISSUE[outcome] ?? outcome}
        </span>
    );
}

function DetailSynchro({ entries, isLoading, erreur }) {
    if (isLoading) {
        return <p className="has-text-grey">Chargement du détail…</p>;
    }

    if (erreur) {
        return <p className="has-text-danger">{erreur}</p>;
    }

    if (entries.length === 0) {
        return <p className="has-text-grey">Aucun détail pour cette synchronisation.</p>;
    }

    return (
        <table className="table is-fullwidth is-narrow is-striped">
            <thead>
                <tr>
                    <th>Issue</th>
                    <th>Album</th>
                    <th>Tome</th>
                    <th>Précision</th>
                </tr>
            </thead>
            <tbody>
                {entries.map((entry) => (
                    <tr key={entry.id}>
                        <td><Issue outcome={entry.outcome} /></td>
                        <td>{entry.comicTitle}</td>
                        <td>{entry.volumeNumber ?? '—'}</td>
                        <td>{entry.detail ?? '—'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function AdminSyncPage() {
    // apiService attend isReady et token : au montage, AuthProvider n'a pas
    // forcément encore posé l'en-tête Authorization sur l'instance axios, et
    // l'appel partirait sans jeton.
    const { token, isReady } = useAuth();
    const [runs, setRuns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [erreur, setErreur] = useState(null);

    // Le détail n'est chargé qu'au dépliage, et conservé ensuite.
    const [runDeplie, setRunDeplie] = useState(null);
    const [details, setDetails] = useState({});
    const [detailEnCours, setDetailEnCours] = useState(false);
    const [erreurDetail, setErreurDetail] = useState(null);

    useEffect(() => {
        if (!isReady || !token) {
            return undefined;
        }

        let annule = false;
        setIsLoading(true);

        apiService.get('/api/admin/import-runs', isReady, token)
            .then((response) => {
                if (!annule) {
                    setRuns(response.data);
                }
            })
            .catch(() => {
                if (!annule) {
                    setErreur('Impossible de récupérer les synchronisations.');
                }
            })
            .finally(() => {
                if (!annule) {
                    setIsLoading(false);
                }
            });

        return () => { annule = true; };
    }, [isReady, token]);

    const basculerDetail = useCallback((runId) => {
        if (runDeplie === runId) {
            setRunDeplie(null);

            return;
        }

        setRunDeplie(runId);
        setErreurDetail(null);

        if (details[runId]) {
            return;
        }

        setDetailEnCours(true);
        apiService.get(`/api/admin/import-runs/${runId}/entries`, isReady, token)
            .then((response) => setDetails((precedents) => ({ ...precedents, [runId]: response.data })))
            .catch(() => setErreurDetail('Impossible de récupérer le détail.'))
            .finally(() => setDetailEnCours(false));
    }, [runDeplie, details, isReady, token]);

    // .hero.is-fullheight .hero-body centre verticalement (align-items:center) :
    // le helper Bulma, qui porte !important, cale le contenu en haut.
    return (
        <div className="hero-body is-align-items-flex-start">
            <div className="container">
                <AdminBreadcrumb trail={[{ libelle: 'Synchronisations' }]} />

                <h1 className="title is-2">Synchronisations du catalogue</h1>
                <h2 className="subtitle">Les dix dernières vagues d'import</h2>

                <p className="mb-5">
                    <a
                        href={URL_CATALOGUE_SOURCE}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <span className="icon"><i className="fas fa-external-link-alt" aria-hidden="true"></i></span>
                        <span>Voir la page crawlée</span>
                    </a>
                </p>

                {isLoading && <p className="has-text-grey">Chargement…</p>}
                {erreur && <div className="notification is-danger is-light">{erreur}</div>}

                {!isLoading && !erreur && runs.length === 0 && (
                    <div className="notification is-info is-light">
                        Aucune synchronisation enregistrée pour le moment.
                    </div>
                )}

                {runs.length > 0 && (
                    <table className="table is-fullwidth is-hoverable">
                        <thead>
                            <tr>
                                <th>Début</th>
                                <th>Dernier message</th>
                                <th className="has-text-right">Vus</th>
                                <th className="has-text-right">Ajoutés</th>
                                <th className="has-text-right">Mis à jour</th>
                                <th className="has-text-right">Inchangés</th>
                                <th className="has-text-right">Échecs</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map((run) => (
                                <Fragment key={run.id}>
                                    <tr>
                                        <td>{formatDate(run.startedAt)}</td>
                                        <td>{formatDate(run.lastMessageAt)}</td>
                                        <td className="has-text-right">{run.seenCount}</td>
                                        <td className="has-text-right">{run.createdCount}</td>
                                        <td className="has-text-right">{run.updatedCount}</td>
                                        <td className="has-text-right has-text-grey">{run.skippedCount}</td>
                                        <td className={`has-text-right ${run.failedCount > 0 ? 'has-text-danger has-text-weight-bold' : ''}`}>
                                            {run.failedCount}
                                        </td>
                                        <td className="has-text-right">
                                            {run.detailCount > 0 && (
                                                <button
                                                    className="button is-small is-ghost"
                                                    onClick={() => basculerDetail(run.id)}
                                                >
                                                    {runDeplie === run.id ? 'Masquer' : `Détail (${run.detailCount})`}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {runDeplie === run.id && (
                                        <tr>
                                            <td colSpan="8">
                                                <DetailSynchro
                                                    entries={details[run.id] ?? []}
                                                    isLoading={detailEnCours}
                                                    erreur={erreurDetail}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default AdminSyncPage;
